import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../App';
import { toast } from 'react-toastify';

function ResultsPage({ session }) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, my-wins

  useEffect(() => {
    fetchResults();
  }, []);

  const fetchResults = async () => {
    setLoading(true);
    try {
      // Fetch completed matches
      const response = await fetch(`${API_BASE}/matches?status=completed`);
      if (response.ok) {
        const matches = await response.json();
        setResults(matches);
      }
    } catch (error) {
      console.error('Error fetching results:', error);
      toast.error('Failed to load results');
    } finally {
      setLoading(false);
    }
  };

  const filteredResults = results.filter(match => {
    if (filter === 'my-wins' && session) {
      return match.results?.some(w => w.user_id === session.user?.id);
    }
    return true;
  });

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
        <p style={{ marginTop: '20px', color: 'var(--text-secondary)' }}>Loading results...</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{
          fontSize: '2rem',
          marginBottom: '10px',
          fontFamily: 'Orbitron, sans-serif',
          color: 'var(--primary-color)'
        }}>
          🏆 Match Results
        </h2>
        <p style={{ color: 'var(--text-secondary)' }}>
          View completed tournament results and winners
        </p>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '30px' }}>
        <button
          className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-outline'}`}
          onClick={() => setFilter('all')}
        >
          All Results
        </button>
        {session && (
          <button
            className={`btn ${filter === 'my-wins' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setFilter('my-wins')}
          >
            My Wins
          </button>
        )}
      </div>

      {/* Results List */}
      {filteredResults.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          background: 'var(--secondary-bg)',
          borderRadius: '12px',
          border: '1px solid var(--border-color)'
        }}>
          <p style={{ fontSize: '4rem', marginBottom: '20px' }}>🏆</p>
          <h3 style={{ fontSize: '1.5rem', marginBottom: '10px' }}>No results yet</h3>
          <p style={{ color: 'var(--text-secondary)' }}>
            Completed match results will appear here
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {filteredResults.map((match) => {
            // If filter is 'my-wins', show ONLY the user's result
            // Otherwise show all results
            let displayResults = match.results || [];
            if (filter === 'my-wins' && session) {
              displayResults = displayResults.filter(w => w.user_id === session.user?.id);
            }

            return (
              <div key={match.id} className="card">
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'start',
                  marginBottom: '20px',
                  flexWrap: 'wrap',
                  gap: '15px'
                }}>
                  <div>
                    <h3 style={{ fontSize: '1.5rem', marginBottom: '8px' }}>{match.title}</h3>
                    <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                        🗺️ {match.map}
                      </span>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                        🎯 {match.mode.toUpperCase()}
                      </span>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                        📅 {new Date(match.start_ts).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <span className="badge badge-success">Completed</span>
                </div>

                <div style={{
                  background: 'var(--accent-bg)',
                  padding: '20px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-color)'
                }}>
                  <h4 style={{
                    fontSize: '1.2rem',
                    marginBottom: '15px',
                    color: 'var(--primary-color)'
                  }}>
                    {filter === 'my-wins' ? '🏆 My Result' : '🏆 Winners'}
                  </h4>
                  {displayResults.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {displayResults.map((winner, idx) => {
                        const isMe = session && (winner.user_id === session.user?.id);
                        const medal = winner.rank === 1 ? '🥇' : winner.rank === 2 ? '🥈' : winner.rank === 3 ? '🥉' : `#${winner.rank}`;
                        const bgColor = winner.rank === 1 ? 'rgba(255, 215, 0, 0.1)' :
                          winner.rank === 2 ? 'rgba(192, 192, 192, 0.1)' :
                            winner.rank === 3 ? 'rgba(205, 127, 50, 0.1)' : 'var(--secondary-bg)';
                        const borderColor = winner.rank === 1 ? 'rgba(255, 215, 0, 0.3)' :
                          winner.rank === 2 ? 'rgba(192, 192, 192, 0.3)' :
                            winner.rank === 3 ? 'rgba(205, 127, 50, 0.3)' : 'var(--border-color)';

                        return (
                          <div key={idx} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '12px',
                            background: isMe ? 'rgba(0, 240, 255, 0.15)' : bgColor,
                            borderRadius: '6px',
                            border: `1px solid ${isMe ? 'var(--primary-color)' : borderColor}`,
                            alignItems: 'center'
                          }}>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                              <span style={{ fontSize: '1.2rem' }}>{medal}</span>
                              <div>
                                <span style={{ fontWeight: isMe ? 'bold' : 'normal', color: isMe ? 'var(--primary-color)' : 'inherit' }}>
                                  {winner.winner_name || 'Player'}
                                </span>
                                {isMe && <span className="badge" style={{ marginLeft: '8px', fontSize: '0.7rem' }}>YOU</span>}
                              </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <span style={{ display: 'block', fontWeight: 'bold', color: 'var(--success-color)' }}>
                                ₹{(winner.prize_amount ? winner.prize_amount / 100 : 0).toLocaleString()}
                              </span>
                              {(winner.finish_points !== undefined) && (
                                <span style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                                  ⚔️ {winner.finish_points} Pts
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                      Results pending...
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ResultsPage;
